import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { DataTableShell, EmptyState, InlineMessage, StatusChip, TableSkeleton } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import { integrationApiService, PriceLedgerEntryDto } from '../../services/integrationApi';
import { tokens } from '../../theme/tokens';
import { filterLedgerEntries, humanizeLedgerOutcome, ledgerCounts, ledgerOutcomeKeys, LedgerFilter } from './shopifyPriceLedger';

interface ShopifyPriceLedgerSectionProps {
  integrationId: string;
  runId: string;
}

const money = (value: number | null): string => (value === null ? '—' : `$${value.toFixed(2)}`);

/**
 * The Shopify price ledger of one sync run (flag `shopify_price_ledger`, MOT-500): every variant price
 * and compare-at this run wrote, or a price guard refused, with the live value before it. Shown inside
 * an expanded run on the integration's Sync history; the CSV is the whole run.
 */
export const ShopifyPriceLedgerSection: React.FC<ShopifyPriceLedgerSectionProps> = ({ integrationId, runId }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [filter, setFilter] = useState<LedgerFilter>('all');
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ['priceLedger', integrationId, runId],
    queryFn: ({ pageParam }) => integrationApiService.getPriceLedgerRows(integrationId, runId, pageParam ?? null),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const entries: PriceLedgerEntryDto[] = useMemo(
    () => (query.data?.pages || []).flatMap((page) => page.entries),
    [query.data],
  );
  const truncatedProducts = (query.data?.pages || []).reduce((sum, page) => sum + page.truncatedProducts, 0);
  const counts = useMemo(() => ledgerCounts(entries), [entries]);
  const visible = useMemo(() => filterLedgerEntries(entries, filter, search), [entries, filter, search]);
  const productCount = useMemo(() => new Set(entries.map((e) => e.productId)).size, [entries]);

  const download = async () => {
    setDownloading(true);
    try {
      await integrationApiService.downloadPriceLedgerCsv(integrationId, runId);
    } catch (error) {
      showToast((error as Error).message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const outcomeLabel = (outcome: string) => {
    const keys = ledgerOutcomeKeys(outcome);
    return keys ? t(`integrations.priceLedger.${keys.label}`) : humanizeLedgerOutcome(outcome);
  };
  const outcomeHint = (outcome: string) => {
    const hint = ledgerOutcomeKeys(outcome)?.hint;
    return hint ? t(`integrations.priceLedger.${hint}`) : '';
  };

  const emptyMessage = search.trim()
    ? t('integrations.priceLedger.emptySearch', { search: search.trim() })
    : filter === 'written'
      ? t('integrations.priceLedger.emptyWritten')
      : filter === 'refused'
        ? t('integrations.priceLedger.emptyRefused')
        : t('integrations.priceLedger.emptyAll');

  const toolbar = (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
        <Box>
          <Typography variant="subtitle2">{t('integrations.priceLedger.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('integrations.priceLedger.coverage', {
              rows: counts.all,
              products: productCount,
              written: counts.written,
              refused: counts.refused,
            })}
          </Typography>
          {counts.refused > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
              {t('integrations.priceLedger.refusedNote')}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={download}
          disabled={downloading || counts.all === 0}
          title={counts.all === 0 ? t('integrations.priceLedger.downloadDisabled') : undefined}
        >
          {t('integrations.priceLedger.download')}
        </Button>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_e, next: LedgerFilter | null) => next && setFilter(next)}
          aria-label={t('integrations.priceLedger.filterLabel')}
        >
          <ToggleButton value="all">{t('integrations.priceLedger.filterAll', { count: counts.all })}</ToggleButton>
          <ToggleButton value="written">{t('integrations.priceLedger.filterWritten', { count: counts.written })}</ToggleButton>
          <ToggleButton value="refused" sx={counts.refused > 0 ? { color: 'warning.main' } : undefined}>
            {t('integrations.priceLedger.filterRefused', { count: counts.refused })}
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('integrations.priceLedger.searchPlaceholder')}
          sx={{ minWidth: { sm: 260 } }}
        />
      </Stack>
      {truncatedProducts > 0 && (
        <InlineMessage severity="info">{t('integrations.priceLedger.truncated', { count: truncatedProducts })}</InlineMessage>
      )}
    </Stack>
  );

  if (query.isError) {
    return (
      <InlineMessage severity="error">
        {(query.error as Error)?.message || t('integrations.priceLedger.loadError')}{' '}
        <Button size="small" onClick={() => query.refetch()}>{t('integrations.priceLedger.retry')}</Button>
      </InlineMessage>
    );
  }

  return (
    <DataTableShell toolbar={toolbar}>
      {query.isLoading ? (
        <TableSkeleton rows={4} columns={7} />
      ) : visible.length === 0 ? (
        <EmptyState size="compact" title={emptyMessage} />
      ) : (
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('integrations.priceLedger.colProduct')}</TableCell>
              <TableCell>{t('integrations.priceLedger.colVariant')}</TableCell>
              <TableCell>{t('integrations.priceLedger.colField')}</TableCell>
              <TableCell align="right">{t('integrations.priceLedger.colOld')}</TableCell>
              <TableCell align="right">{t('integrations.priceLedger.colNew')}</TableCell>
              <TableCell>{t('integrations.priceLedger.colOutcome')}</TableCell>
              <TableCell>{t('integrations.priceLedger.colSource')}</TableCell>
              <TableCell>{t('integrations.priceLedger.colTime')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((entry, index) => (
              <TableRow key={`${entry.productId}-${entry.variantId}-${entry.field}-${index}`} sx={{ height: 48 }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{entry.productName || entry.productId}</Typography>
                  {entry.productSku && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: tokens.font.mono }}>{entry.productSku}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: tokens.font.mono }}>{entry.variantId}</TableCell>
                <TableCell>
                  {entry.field === 'price' ? t('integrations.priceLedger.fieldPrice') : t('integrations.priceLedger.fieldCompareAt')}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(entry.oldValue)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(entry.newValue)}</TableCell>
                <TableCell>
                  {(() => {
                    const hint = outcomeHint(entry.outcome);
                    const chip = (
                      <StatusChip
                        size="small"
                        label={outcomeLabel(entry.outcome)}
                        tone={entry.outcome === 'written' ? 'positive' : entry.outcome === 'write_failed' ? 'problem' : 'caution'}
                      />
                    );
                    return hint ? <Tooltip title={hint}><span>{chip}</span></Tooltip> : chip;
                  })()}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{entry.source || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{new Date(entry.at).toLocaleString()}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {query.hasNextPage && (
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
            {t('integrations.priceLedger.loadMore')}
          </Button>
        </Box>
      )}
    </DataTableShell>
  );
};

export default ShopifyPriceLedgerSection;
